import {
  createSlice,
  createAsyncThunk,
  createSelector,
  PayloadAction,
} from "@reduxjs/toolkit";
import { getFormEntries } from "../../services/apiService";
import { FormEntry } from "../../types/types";
import { RootState } from "../index";
import { formMappings } from "../../config/formMappings";
import { getRegistrantsContainer } from "../../utils/utils";

// Async thunk to fetch form entries
export const fetchFormEntries = createAsyncThunk<
  FormEntry[],
  string,
  { rejectValue: string; state: RootState }
>(
  "formEntries/fetchFormEntries",
  async (formId, { rejectWithValue, getState }) => {
    // Check if we already have entries for this formId
    const state = getState();
    if (
      state.formEntries.loadedFormId === formId &&
      state.formEntries.allEntries.length > 0
    ) {
      // Return existing entries without making API call
      return state.formEntries.allEntries;
    }

    try {
      const data = await getFormEntries(formId);
      return data;
    } catch (error) {
      return rejectWithValue(
        (error as Error).message || "An unknown error occurred."
      );
    }
  }
);

// Explicitly type the initial state
const initialState: {
  allEntries: FormEntry[];
  entries: FormEntry[];
  loading: boolean;
  error: string | null;
  activeFormName: string | null;
  loadedFormId: string | null; // Track which formId has been loaded
} = {
  allEntries: [],
  entries: [],
  loading: false,
  error: null,
  activeFormName: "2025 Conference Registration", // Default to match activeFormSlice
  loadedFormId: null,
};

// Slice for form entries
const formEntriesSlice = createSlice({
  name: "formEntries",
  initialState,
  reducers: {
    resetState: () => ({ ...initialState }), // Use spread to ensure immutability
    setActiveFormName: (state, action: PayloadAction<string>) => {
      state.activeFormName = action.payload;
    },
    clearLoadedFormId: (state) => {
      state.loadedFormId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFormEntries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFormEntries.fulfilled, (state, action) => {
        state.loading = false;
        state.allEntries = action.payload;

        // Track which formId was loaded (action.meta.arg contains the formId passed to the thunk)
        state.loadedFormId = action.meta.arg;

        // Get paid field key from form mapping and filter entries
        let paidFieldKey: string | null = null;

        if (state.activeFormName) {
          const formMapping =
            formMappings[state.activeFormName as keyof typeof formMappings];
          if (formMapping?.fieldMappings?.paid) {
            paidFieldKey = formMapping.fieldMappings.paid;
          }
        }

        if (paidFieldKey) {
          state.entries = action.payload.filter((entry) => {
            // Check both the field key and "Paid?" (in case API service mapped it)
            return (
              entry.meta[paidFieldKey!] === "Yes" ||
              entry.meta["Paid?"] === "Yes"
            );
          });
        } else {
          // Fallback: if no paid field key found, return all entries
          console.warn("No paid field key found, returning all entries");
          state.entries = action.payload;
        }
      })
      .addCase(fetchFormEntries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Selector to find a specific entry by its key
export const selectEntryByKey = (state: RootState, key: string) => {
  return state.formEntries.allEntries.find((entry) => entry.item_key === key);
};

// Helper function to check if an entry is paid
function isEntryPaid(entry: FormEntry, activeFormName: string | null): boolean {
  if (!activeFormName) return false;

  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const paidFieldKey = formMapping?.fieldMappings?.paid;

  if (paidFieldKey) {
    return entry.meta[paidFieldKey] === "Yes" || entry.meta["Paid?"] === "Yes";
  }

  return false;
}

// Selector to find entries by exact email match with priority:
// 1. Paid entries first
// 2. If none paid, latest entry
export const selectEntryByEmail = createSelector(
  [
    (state: RootState) => state.formEntries.allEntries,
    (state: RootState) => state.formEntries.activeFormName,
    (_: RootState, email: string) => email.toLowerCase(),
  ],
  (entries, activeFormName, email) => {
    if (!email) return null;

    // Get email field key from form mapping
    const formMapping = activeFormName
      ? formMappings[activeFormName as keyof typeof formMappings]
      : null;
    const emailFieldKey = formMapping?.fieldMappings?.email || "jgfy3";

    // Find all entries with matching email
    const matchingEntries: FormEntry[] = [];

    entries.forEach((entry) => {
      const emails: string[] = [];

      // Check if Email exists at entry level
      if (entry.meta.Email) {
        if (Array.isArray(entry.meta.Email)) {
          emails.push(...entry.meta.Email.map((e) => String(e).toLowerCase()));
        } else {
          emails.push(String(entry.meta.Email).toLowerCase());
        }
      }

      // Extract emails from registrants
      const registrantsContainer = getRegistrantsContainer(entry.meta);
      if (registrantsContainer) {
        Object.entries(registrantsContainer)
          .filter(([key]) => key !== "form")
          .forEach(([_, registrant]: [string, any]) => {
            const registrantEmail = registrant[emailFieldKey];
            if (registrantEmail) {
              if (Array.isArray(registrantEmail)) {
                emails.push(
                  ...registrantEmail.map((e) => String(e).toLowerCase())
                );
              } else {
                emails.push(String(registrantEmail).toLowerCase());
              }
            }
          });
      }

      // Check if email matches exactly (case-insensitive)
      if (emails.includes(email)) {
        matchingEntries.push(entry);
      }
    });

    if (matchingEntries.length === 0) return null;

    // Separate paid and unpaid entries
    const paidEntries = matchingEntries.filter((entry) =>
      isEntryPaid(entry, activeFormName)
    );
    const unpaidEntries = matchingEntries.filter(
      (entry) => !isEntryPaid(entry, activeFormName)
    );

    // Priority: paid entries first
    if (paidEntries.length > 0) {
      // Sort paid entries by created_at (latest first)
      return paidEntries.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];
    }

    // If no paid entries, return latest unpaid entry
    if (unpaidEntries.length > 0) {
      return unpaidEntries.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];
    }

    return null;
  }
);

// Memoized selector for entries filtered by email (case-insensitive)
export const selectEntriesByItemKeyOrEmail = createSelector(
  [
    (state: RootState) => state.formEntries.allEntries,
    (state: RootState) => state.formEntries.activeFormName,
    (_: RootState, searchKey: string) => searchKey.toLowerCase(),
  ],
  (entries, activeFormName, searchKey) => {
    if (!searchKey) return [];

    // Get email field key from form mapping
    const formMapping = activeFormName
      ? formMappings[activeFormName as keyof typeof formMappings]
      : null;
    const emailFieldKey = formMapping?.fieldMappings?.email || "jgfy3";

    return entries.filter((entry) => {
      const emails: string[] = [];

      // First, check if Email exists at entry level (for some entry types like bazaar)
      if (entry.meta.Email) {
        if (Array.isArray(entry.meta.Email)) {
          emails.push(...entry.meta.Email);
        } else {
          emails.push(String(entry.meta.Email));
        }
      }

      // Also extract emails from registrants
      const registrantsContainer = getRegistrantsContainer(entry.meta);
      if (registrantsContainer) {
        Object.entries(registrantsContainer)
          .filter(([key]) => key !== "form")
          .forEach(([_, registrant]: [string, any]) => {
            const email = registrant[emailFieldKey];
            if (email) {
              // Handle both string and array email values
              if (Array.isArray(email)) {
                emails.push(...email);
              } else {
                emails.push(String(email));
              }
            }
          });
      }

      // Search through all extracted emails
      return (
        emails.length > 0 &&
        emails.some((email: string) => email.toLowerCase().includes(searchKey))
      );
    });
  }
);

export const { resetState, setActiveFormName: setFormEntriesActiveFormName } =
  formEntriesSlice.actions;
export default formEntriesSlice.reducer;
